<template>
  <div v-if="envName" class="env-banner">{{ envName }} Environment</div>
  <router-view />
  <ToastContainer />
  <SessionExpiredModal />
  <VersionUpdateModal />
</template>

<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { usePasswordChangeRedirect } from '@monorepo/composables';
import { getEnvironment, isProduction } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { useMerchantStore } from '@/stores/merchant';
import { useTheme } from '@/composables/useTheme';
import { useVersionCheck } from '@/composables/useVersionCheck';
import { ToastContainer } from '@/components/ui/toast';
import SessionExpiredModal from '@/components/SessionExpiredModal.vue';
import VersionUpdateModal from '@/components/VersionUpdateModal.vue';

const envName = computed(() => (isProduction() ? null : getEnvironment().toUpperCase()));

const authStore = useAuthStore();
const merchantStore = useMerchantStore();
const { initializeTheme, loadMerchantTheme } = useTheme();

// Check for new deployments (production only)
const { start: startVersionCheck } = useVersionCheck();
startVersionCheck();

// A session whose token predates `must_change_password` is refused by the RPC
// middleware wherever it happens to be (PCI DSS 8.3.5, #1198); the RPC client
// raises an event on that refusal. Owned here, with teardown, exactly like the
// `session-expired` listener in SessionExpiredModal.vue. The store flag is
// passed too so a background permissions refresh that discovers the gate
// redirects immediately, rather than waiting for a navigation or a refusal.
usePasswordChangeRedirect(useRouter(), {
  mustChangePassword: computed(() => authStore.mustChangePassword),
});

onMounted(async () => {
  // Initialize theme (dark/light mode)
  initializeTheme();

  await authStore.initializeAuth();
});

// Watch for merchant changes to apply custom theme
watch(
  () => merchantStore.currentMerchant,
  (merchant) => {
    if (merchant?.settings) {
      loadMerchantTheme(merchant.settings);
    } else {
      loadMerchantTheme(null);
    }
  },
  { immediate: true }
);
</script>
